import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Plus } from "lucide-react";
import { ApiError, api, type CvDocument, type Resume } from "../lib/api.js";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";

/** Verified CV builder — list + create (optionally from a parsed resume). REQ-2.7. */
export function CvListPage() {
  const [cvs, setCvs] = useState<CvDocument[] | null>(null);
  const [resumes, setResumes] = useState<Resume[]>([]);

  function refresh() {
    void api.listCvs().then((r) => setCvs(r.cvs));
  }
  useEffect(() => {
    refresh();
    void api.listResumes().then((r) => setResumes(r.resumes.filter((x) => x.parsed)));
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Verified CVs</h1>

      <CreateCv resumes={resumes} onCreated={refresh} />

      {!cvs ? (
        <div className="h-32 animate-pulse rounded-[var(--radius-lg)] bg-secondary" />
      ) : cvs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No CVs yet — create one above.</p>
      ) : (
        <div className="space-y-2">
          {cvs.map((cv) => (
            <Card key={cv.id}>
              <CardContent className="flex items-center gap-4 p-4">
                <FileText size={20} className="shrink-0 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{cv.title}</p>
                  <p className="truncate text-xs text-muted-foreground">/cv/{cv.slug}</p>
                </div>
                <Badge variant={cv.publishedAt ? "verified" : "neutral"}>
                  {cv.publishedAt ? "Published" : "Draft"}
                </Badge>
                <Button asChild size="sm" variant="secondary">
                  <Link to={`/cv/${cv.id}`}>Edit</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateCv({ resumes, onCreated }: { resumes: Resume[]; onCreated: () => void }) {
  const [slug, setSlug] = useState("");
  const [title, setTitle] = useState("");
  const [resumeId, setResumeId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setError(null);
    setBusy(true);
    try {
      await api.createCv({
        slug: slug.trim(),
        title: title.trim() || undefined,
        resumeId: resumeId || undefined,
      });
      setSlug("");
      setTitle("");
      setResumeId("");
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create CV");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>New CV</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <Label>Title</Label>
            <Input placeholder="My CV" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label>URL slug</Label>
            <Input placeholder="jane-doe" value={slug} onChange={(e) => setSlug(e.target.value)} />
          </div>
          <div>
            <Label>From resume</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-card px-3 text-sm"
              value={resumeId}
              onChange={(e) => setResumeId(e.target.value)}
            >
              <option value="">Blank</option>
              {resumes.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.fileName ?? r.id.slice(0, 8)}
                </option>
              ))}
            </select>
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button onClick={() => void submit()} disabled={busy || slug.trim().length < 3}>
          <Plus size={16} /> Create CV
        </Button>
      </CardContent>
    </Card>
  );
}
